<template>
  <!-- WERSJA NORMALNA -->
  <div v-if="!docked" class="widget-content">
    <div class="card card--pretty">
      <div class="card-header py-2 px-3 d-flex align-items-center justify-content-between">
        <div class="small text-muted">
          Użyte w zapytaniu: <strong>{{ usedInQuoteRequest.join(', ') || '—' }}</strong> •
          Użyte w zamówieniu: <strong>{{ usedInOrder.join(', ') || '—' }}</strong>
        </div>
        <button class="btn btn-xs btn-outline-secondary" @click="toggleDock">
          <i class="bi bi-pin-angle me-1"></i> Przypnij na dole
        </button>
      </div>

      <div class="card-body p-2">

        <!-- Identyfikacja -->
        <div class="section">
          <div class="section-title">Identyfikacja</div>
          <table class="tbl-condensed">
            <tbody>
            <tr>
              <th>Projekt</th>
              <td>{{ selectedRecord.project?.number || '—' }}</td>
              <th>Nr poz.</th>
              <td>{{ selectedRecord.id }}</td>
              <th>Barcode</th>
              <td class="truncate" :title="selectedRecord.barcode">{{ selectedRecord.barcode || '—' }}</td>
            </tr>
            <tr>
              <th>Zespół</th>
              <td class="truncate" :title="selectedRecord.team_name">{{ selectedRecord.team_name || '—' }}</td>
              <th>Podzespół</th>
              <td class="truncate" :title="selectedRecord.subteam_drawing_no">
                {{ selectedRecord.subteam_drawing_no || '—' }}
              </td>
              <th>Edytowano</th>
              <td><span v-if="selectedRecord.was_edited_later" class="badge bg-danger">Tak</span><span v-else>Nie</span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Artykuł -->
        <div class="section">
          <div class="section-title">Artykuł</div>
          <table class="tbl-condensed">
            <tbody>
            <tr>
              <th>Artykuł ID</th>
              <td>{{ selectedRecord.article?.id || '—' }}</td>
              <th>Nr katalogowy</th>
              <td class="truncate" :title="selectedRecord.article?.catalog_number">
                {{ selectedRecord.article?.catalog_number || '—' }}
              </td>
              <th>Model</th>
              <td class="truncate" :title="selectedRecord.article?.model">{{
                  selectedRecord.article?.model || '—'
                }}
              </td>
            </tr>
            <tr>
              <th>Opis</th>
              <td class="truncate" :title="selectedRecord.article?.description" colspan="3">
                {{ selectedRecord.article?.description || '—' }}
              </td>
              <th>J.m.</th>
              <td>{{ unitName }}</td>
            </tr>
            <tr>
              <th>Producent</th>
              <td class="truncate" :title="producerName">{{ producerName }}</td>
              <th>Kategoria</th>
              <td class="truncate" :title="categoryName">{{ categoryName }}</td>
              <th>Link</th>
              <td class="truncate" :title="selectedRecord.article?.link">
                <a v-if="selectedRecord.article?.link" :href="selectedRecord.article.link" target="_blank"
                   rel="noopener">{{ trimmedLink }}</a>
                <span v-else>—</span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Ilości / Magazyn -->
        <div class="section">
          <div class="section-title">Ilości / Magazyn</div>
          <table class="tbl-condensed">
            <tbody>
            <tr>
              <th>Ilość wymagana</th>
              <td>{{ selectedRecord.quantity_required ?? '—' }}</td>
              <th>Ilość maszyn</th>
              <td>{{ selectedRecord.numbering?.quantity ?? '—' }}</td>
              <th>Ilość do zamówienia</th>
              <td>{{ qtyToOrder }}</td>
            </tr>
            <tr>
              <th>Ilość magazynowa</th>
              <td>{{ selectedRecord.article_main_warehouse?.quantity ?? '—' }}</td>
              <th>Ilość dostarczona</th>
              <td>{{ selectedRecord.article_project_warehouse?.quantity ?? '—' }}</td>
              <th>Magazyn</th>
              <td class="truncate" :title="selectedRecord.warehouse_received">
                {{ selectedRecord.warehouse_received || '—' }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Status / Daty -->
        <div class="section">
          <div class="section-title">Status / Daty</div>
          <table class="tbl-condensed">
            <tbody>
            <tr>
              <th>Status</th>
              <td>{{ selectedRecord.status || '—' }}</td>
              <th>Status mag.</th>
              <td>{{ selectedRecord.inventory_status?.name || '—' }}</td>
              <th>Data prod.</th>
              <td>{{ formatDate(selectedRecord.numbering?.production_date) }}</td>
            </tr>
            <tr>
              <th>Data dostawy</th>
              <td>{{ formatDate(selectedRecord.delivery_date) }}</td>
              <th>Data zwolnienia</th>
              <td>{{ formatDate(selectedRecord.release_date) }}</td>
              <th>Czas dostawy</th>
              <td>{{ formatDate(selectedRecord.lead_time) }}</td>
            </tr>
            <tr>
              <th>Zlec. zamówienia</th>
              <td>{{ formatDate(selectedRecord.order_submission_date) }}</td>
              <th>Wydruk zamówienia</th>
              <td>{{ formatDate(selectedRecord.order_status_confirmation_date) }}</td>
              <th>Przekroczenie</th>
              <td>
                <span v-if="lateDays>0" class="text-danger">{{ lateDays }} dni</span>
                <span v-else>—</span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Powiązania / Pliki -->
        <div class="section">
          <div class="section-title">Powiązania / Pliki</div>
          <table class="tbl-condensed">
            <tbody>
            <tr>
              <th>Dostawcy</th>
              <td class="truncate" :title="selectedRecord.suppliers_short_names" colspan="3">
                {{ selectedRecord.suppliers_short_names || '—' }}
              </td>
              <th>Patent</th>
              <td>{{ selectedRecord.patent_number || '—' }}</td>
            </tr>
            <tr>
              <th>Zapytania ofert.</th>
              <td class="truncate" :title="rowQuoteRequests">{{ rowQuoteRequests }}</td>
              <th>Zamówienia</th>
              <td class="truncate" :title="rowOrders">{{ rowOrders }}</td>
              <th>DW</th>
              <td class="truncate" :title="rowDW">{{ rowDW }}</td>
            </tr>
            <tr>
              <th>Załączniki art.</th>
              <td>{{ selectedRecord.article?.files?.length || 0 }}</td>
              <th>Załączniki zapotrz.</th>
              <td>{{ selectedRecord.files?.length || 0 }}</td>
              <th></th>
              <td></td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Meta -->
        <div class="section">
          <div class="section-title">Meta</div>
          <table class="tbl-condensed">
            <tbody>
            <tr>
              <th>Dodany przez</th>
              <td>{{ selectedRecord.created_by?.name || '—' }}</td>
              <th>Dodano</th>
              <td>{{ formatDate(selectedRecord.created_at) }}</td>
              <th>Ostatnio zmienił</th>
              <td>{{ selectedRecord.updated_by?.name || '—' }}</td>
            </tr>
            <tr>
              <th>Uwagi</th>
				<td class="truncate" colspan="5">
				  <div
					v-if="selectedRecord.comments"
					class="comments"
					v-html="formattedComments"
				  ></div>
				  <span v-else>—</span>
				</td>
            </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>
  </div>

  <!-- WERSJA DOKOWANA: FIXED na dole -->
  <teleport to="body">
    <div v-if="docked" class="dock-panel shadow">
      <div class="dock-header">
        <div class="small text-muted">
          Użyte w zapytaniu: <strong>{{ usedInQuoteRequest.join(', ') || '—' }}</strong> •
          Użyte w zamówieniu: <strong>{{ usedInOrder.join(', ') || '—' }}</strong>
        </div>
        <div class="dock-actions">
          <button class="btn btn-xs btn-outline-danger" @click="toggleDock">
            <i class="bi bi-x-lg"></i> Zamknij
          </button>
        </div>
      </div>

      <div class="dock-body">
         <div class="details-row">
          <span class="kv"><b>Projekt</b><i>{{ selectedRecord.project?.number || '—' }}</i></span>
          <span class="kv"><b>Nr poz.</b><i>{{ selectedRecord.id }}</i></span>
          <span class="kv"><b>Zespół</b><i class="ellipsis" :title="selectedRecord.team_name">{{
              selectedRecord.team_name || '—'
            }}</i></span>
          <span class="kv"><b>Podzesp.</b><i class="ellipsis" :title="selectedRecord.subteam_drawing_no">{{
              selectedRecord.subteam_drawing_no || '—'
            }}</i></span>
          <span class="kv"><b>Barcode</b><i>{{ selectedRecord.barcode || '—' }}</i></span>
          <span class="kv" v-if="selectedRecord.was_edited_later"><b>!</b><i
              class="text-danger fw-bold">Edytowano</i></span>
        <span class="kv"><b>Artykuł ID</b><i>{{ selectedRecord.article?.id || '—' }}</i></span>
          <span class="kv"><b>Katalog</b><i class="ellipsis" :title="selectedRecord.article?.catalog_number">{{
              selectedRecord.article?.catalog_number || '—'
            }}</i></span>
        </div>

        <div class="details-row">
          <span class="kv"><b>Model</b><i class="ellipsis" :title="selectedRecord.article?.model">{{
              selectedRecord.article?.model || '—'
            }}</i></span>
          <span class="kv"><b>J.m.</b><i>{{ unitName }}</i></span>
          <span class="kv"><b>Producent</b><i>{{ producerName }}</i></span>
          <span class="kv"><b>Kategoria</b><i>{{ categoryName }}</i></span>
          <span class="kv"><b>Ilość wym.</b><i>{{ selectedRecord.quantity_required ?? '—' }}</i></span>
          <span class="kv"><b>Ilość maszyn</b><i>{{ selectedRecord.numbering?.quantity ?? '—' }}</i></span>
          <span class="kv"><b>Ilość do zam.</b><i>{{ qtyToOrder }}</i></span>
        </div>

        <div class="details-row">          
		  <span class="kv"><b>Magazynowa</b><i>{{ selectedRecord.article_main_warehouse?.quantity ?? '—' }}</i></span>
          <span class="kv"><b>Dostarczona</b><i>{{
              selectedRecord.article_project_warehouse?.quantity ?? '—'
            }}</i></span>
          <span class="kv"><b>Magazyn</b><i>{{ selectedRecord.warehouse_received || '—' }}</i></span>
          <span class="kv"><b>Status</b><i>{{ selectedRecord.status || '—' }}</i></span>
          <span class="kv"><b>Status mag.</b><i>{{ selectedRecord.inventory_status?.name || '—' }}</i></span>
          <span class="kv"><b>Dostawa</b><i>{{ formatDate(selectedRecord.delivery_date) }}</i></span>
          <span class="kv"><b>Zwolnienie</b><i>{{ formatDate(selectedRecord.release_date) }}</i></span>
        </div>

        <div class="details-row">          
		  <span class="kv"><b>Zlec. zam.</b><i>{{ formatDate(selectedRecord.order_submission_date) }}</i></span>
          <span class="kv"><b>Wydruk zam.</b><i>{{
              formatDate(selectedRecord.order_status_confirmation_date)
            }}</i></span>
          <span class="kv"><b>Data prod.</b><i>{{ formatDate(selectedRecord.numbering?.production_date) }}</i></span>
          <span class="kv"><b>Czas dost.</b><i>{{ formatDate(selectedRecord.lead_time) }}</i></span>
          <span class="kv" v-if="lateDays>0"><b>Przekr.</b><i class="text-danger">{{ lateDays }} dni</i></span>
          <span class="kv kv--wide"><b>Dostawcy</b><i class="ellipsis" :title="selectedRecord.suppliers_short_names">{{
              selectedRecord.suppliers_short_names || '—'
            }}</i></span>
          <span class="kv"><b>Zap. ofert.</b><i>{{ rowQuoteRequests }}</i></span>
          <span class="kv"><b>Zamówienia</b><i>{{ rowOrders }}</i></span>
        </div>

        <div class="details-row">          
		  <span class="kv"><b>DW</b><i>{{ rowDW }}</i></span>
          <span class="kv kv--link" v-if="selectedRecord.article?.link"><b>Link</b><i><a
              :href="selectedRecord.article.link" target="_blank" rel="noopener">{{ trimmedLink }}</a></i></span>
          <span class="kv"><b>Dodany przez</b><i>{{ selectedRecord.created_by?.name || '—' }}</i></span>
          <span class="kv"><b>Dodano</b><i>{{ formatDate(selectedRecord.created_at) }}</i></span>
          <span class="kv"><b>Ostatnio zmienił</b><i>{{ selectedRecord.updated_by?.name || '—' }}</i></span>
        </div>
       <div class="details-row">
 		  <span class="kv kv--xxl"><b>Opis</b><i class="ellipsis" :title="selectedRecord.article?.description">{{
              selectedRecord.article?.description || '—'
            }}</i></span>
        </div>		
		<div class="details-row">
		  <span class="kv kv--xxl"><b>Uwagi</b>
		    <i class="ellipsis" :title="selectedRecord.comments">
		    <span v-if="selectedRecord.comments" v-html="formattedComments"></span>
		    <span v-else>—</span>
			</i>
		  </span>
        </div>		
      </div>
    </div>
  </teleport>
</template>

<script>
export default {
  props: {selectedRecord: Object},
  data() {
    return {
      usedInQuoteRequest: [],
      usedInOrder: [],
      docked: false,
    };
  },
computed: {
  formattedComments() {
    const text = this.selectedRecord?.comments || '';

    const urlRegex = /(https?:\/\/[^\s]+)/g;

    return text.replace(urlRegex, (url) => {
      let short = url;

      // skrócenie bardzo długiego linku
      if (url.length > 40) {
        short = url.substring(0, 25) + '...' + url.substring(url.length - 10);
      }

      return `<a href="${url}" target="_blank" rel="noopener">${short}</a>`;
    });
  },

    unitName() {
      return this.selectedRecord?.article?.article_unit?.name
          || this.selectedRecord?.article_unit?.name
          || '—';
    },
    producerName() {
      const p = this.selectedRecord?.article?.producer;
      return p?.short_name || p?.name || this.selectedRecord?.article?.producer_id || '—';
    },
    categoryName() {
      const c = this.selectedRecord?.article?.customer_category;
      return c?.name || this.selectedRecord?.article?.customer_category_id || '—';
    },
    qtyToOrder() {
      const q = Number(this.selectedRecord?.quantity_required || 0);
      const m = Number(this.selectedRecord?.numbering?.quantity || 0);
      return (q * m) || 0;
    },
    rowQuoteRequests() {
      const arr = this.selectedRecord?.quote_request_items || [];
      return arr.map(x => x?.quote_request_number).filter(Boolean).join(', ') || '—';
    },
    rowOrders() {
      const arr = this.selectedRecord?.order_lists || [];
      return arr.map(x => x?.order_number).filter(Boolean).join(', ') || '—';
    },
    rowDW() {
      const arr = this.selectedRecord?.dw_warehouse_movements || [];
      return arr.map(x => x?.document_number).filter(Boolean).join(', ') || '—';
    },
    lateDays() {
      const prod = this.selectedRecord?.numbering?.production_date ? new Date(this.selectedRecord.numbering.production_date) : null;
      const del = this.selectedRecord?.delivery_date ? new Date(this.selectedRecord.delivery_date) : null;
      if (!prod || !del || isNaN(prod) || isNaN(del)) return 0;
      const diff = prod - del;
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
      return days < 0 ? Math.abs(days) : 0;
    },
    trimmedLink() {
      const l = this.selectedRecord?.article?.link || '';
      return l.length > 40 ? l.slice(0, 38) + '…' : l;
    }
  },
  watch: {
    selectedRecord: {
      immediate: true,
      deep: true,
      async handler(v) {
        if (v?.id) {
          await this.getUsedInQuoteRequest();
          await this.getUsedInOrder();
        } else {
          this.usedInQuoteRequest = [];
          this.usedInOrder = [];
        }
      }
    }
  },
  methods: {
    toggleDock() {
      this.docked = !this.docked;
    },
    formatDate(d) {
      if (!d) return '—';
      const dt = new Date(d);
      if (isNaN(dt)) return '—';
      const dd = String(dt.getDate()).padStart(2, '0');
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const yyyy = dt.getFullYear();
      return `${dd}.${mm}.${yyyy}`;
    },
    async getUsedInQuoteRequest() {
      try {
        this.usedInQuoteRequest = [];
        const resp = await axios.get(route('requiredProjectItems.get-use-quote-requests', {
          requiredProjectItem: this.selectedRecord.id
        }));
        this.usedInQuoteRequest = (resp.data || [])
            .map(i => i?.quote_request?.request_number)
            .filter(Boolean);
      } catch (e) {
        console.error(e);
      }
    },
    async getUsedInOrder() {
      try {
        this.usedInOrder = [];
        const resp = await axios.get(route('requiredProjectItems.get-use-orders', {
          requiredProjectItem: this.selectedRecord.id
        }));
        this.usedInOrder = (resp.data || [])
            .map(i => i?.order_list?.order_number)
            .filter(Boolean);
      } catch (e) {
        console.error(e);
      }
    },
  }
}
</script>

<style scoped>
/* ——— normalny widok: lekko skondensowana tabela ——— */
.card--pretty {
  border: 1px solid #e5e7eb;
}

.section {
  margin-bottom: 10px;
}

.section-title {
  font-weight: 600;
  font-size: 13px;
  color: #374151;
  margin: 4px 0;
  padding-left: 2px;
}

.tbl-condensed {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed; /* żeby działały elipsy */
}

.tbl-condensed th,
.tbl-condensed td {
  padding: 6px 8px;
  border: 1px solid #f1f5f9; /* bardzo subtelne */
  vertical-align: middle;
  font-size: 13px;
}

.tbl-condensed th {
  width: 12rem;
  color: #6b7280;
  background: #fafafa;
  font-weight: 600;
}

.tbl-condensed td.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ——— panel dokowany (bez zmian) ——— */
.details-row {
  display: grid;
  grid-template-columns:
    minmax(120px, 1fr)
    minmax(120px, 1fr)
    minmax(180px, 1.5fr)
    minmax(180px, 1.5fr)
    minmax(120px, 1fr)
    minmax(120px, 1fr)
    minmax(120px, 1fr)
    minmax(120px, 1fr);
  gap: 8px;
  padding: 2px 0;
}

.kv {
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
  font-size: 12px;
  line-height: 1.2;
  border-right: 1px dashed #e5e7eb;
  padding-right: 8px;
}

.kv:last-child {
  border-right: 0;
}

.kv > b {
  flex-shrink: 0;
  color: #6b7280;
  font-weight: 600;
}

.kv > i {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: normal;
  font-weight: 600;
  color: #111827;
}

.kv--wide {
  grid-column: span 2;
}

.kv--xxl {
  grid-column: span 3;
}

.kv--link a {
  text-decoration: none;
}

.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
}

.dock-panel {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  max-height: 38vh;
  z-index: 1035;
  display: flex;
  flex-direction: column;
}

.dock-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
}

.dock-actions .btn {
  padding: 2px 6px;
  font-size: 12px;
}

.dock-body {
  padding: 6px 10px;
  overflow: auto;
}
.comments {
  white-space: pre-wrap;
}
</style>
